import { useEffect, useMemo, useState } from "react";
import { fmtMoney, Pill } from "./Master";
import { SweetAlertShow } from "../../../master/Master";
import { formatToDate } from "../../../master/FormatToDateTime";
import { studentTransportNFeeUpdateDiscount } from "../../../../api/StudentDetails";

const MONTH_NAMES = [
  "",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const monthName = (month) => MONTH_NAMES[Number(month)] || month;

const transportStatusMeta = (status) => {
  switch (String(status)) {
    case "1":
      return {
        label: "Active",
        color: "success",
        icon: "ri-checkbox-circle-line",
      };

    case "2":
      return {
        label: "Inactive",
        color: "secondary",
        icon: "ri-close-circle-line",
      };
    default:
      return {
        label: "Unknown",
        color: "secondary",
        icon: "ri-question-line",
      };
  }
};

const transportFeeStatusMeta = (paid, final) => {
  const paidAmount = Number(paid || 0);
  const finalAmount = Number(final || 0);

  if (finalAmount > 0 && paidAmount >= finalAmount) {
    return {
      label: "Paid",
      color: "success",
      icon: "ri-checkbox-circle-line",
    };
  }

  if (paidAmount > 0) {
    return {
      label: "Partial",
      color: "warning",
      icon: "ri-time-line",
    };
  }

  return {
    label: "Due",
    color: "danger",
    icon: "ri-error-warning-line",
  };
};

const num = (value) => {
  const n = Number(value || 0);

  return Number.isFinite(n) ? n : 0;
};

const roundMoney = (value) => {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
};

export default function TransportFeeDetailModal({ entry, onClose, onUpdated }) {
  const [mode, setMode] = useState("view");
  const [saving, setSaving] = useState(false);
  const [feeItems, setFeeItems] = useState([]);
  const [discountData, setDiscountData] = useState({
    type: "fixed",
    value: "",
  });

  const [discountRemark, setDiscountRemark] = useState(
    entry?.discount_remark || "",
  );


  useEffect(() => {
    if (mode === "discount" && entry?.bus_fee_details) {
      const items = entry.bus_fee_details.map((fee) => ({
        id: fee.id,
        label: `${monthName(fee.month)} ${fee.year} (${fee.type})`,
        month: fee.month,
        year: fee.year,
        type: fee.type,
        actual_amount: num(fee.actual_amount),
        discount: num(fee.discount),
        final_amount: num(fee.final_amount),
        paid_amount: num(fee.paid_amount),
        due_amount: num(fee.due_amount),
      }));

      setFeeItems(items);

      setDiscountData({
        type: "fixed",
        value: "",
      });

      setDiscountRemark(entry?.discount_remark || "");
    }
  }, [mode, entry]);

  const totalActual = useMemo(() => {
    return feeItems.reduce((sum, fee) => sum + num(fee.actual_amount), 0);
  }, [feeItems]);


  const totalDiscount = useMemo(() => {
    return feeItems.reduce((sum, fee) => sum + num(fee.discount), 0);
  }, [feeItems]);

  const totalFinal = useMemo(() => {
    return feeItems.reduce((sum, fee) => sum + num(fee.final_amount), 0);
  }, [feeItems]);

  const totalPaid = useMemo(() => {
    return feeItems.reduce((sum, fee) => sum + num(fee.paid_amount), 0);
  }, [feeItems]);

  const totalDue = useMemo(() => {
    return feeItems.reduce(
      (sum, fee) =>
        sum + Math.max(num(fee.final_amount) - num(fee.paid_amount), 0),
      0,
    );
  }, [feeItems]);


  if (!entry) {
    return null;
  }

  const feeDetails = entry?.bus_fee_details || [];

  const statusMeta = transportStatusMeta(entry?.status);

  const calculateDiscount = (type, value) => {
    const actualAmount = num(entry.bus_stop_fee);
    const discountValue = num(value);
    const updatedFees = feeItems.map((fee) => {
      const paidAmount = num(fee.paid_amount);
      let discountAmount = 0;
      if (type === "fixed") {
        discountAmount = Math.min(discountValue, actualAmount);
      }

      if (type === "percentage") {
        const percentage = Math.min(discountValue, 100);
        discountAmount = (actualAmount * percentage) / 100;
      }

      discountAmount = roundMoney(discountAmount);
      let finalAmount = actualAmount - discountAmount;
      finalAmount = Math.max(paidAmount, finalAmount);
      finalAmount = roundMoney(finalAmount);

      const actualDiscount = Math.max(actualAmount - finalAmount, 0);
      const dueAmount = Math.max(finalAmount - paidAmount, 0);

      return {
        ...fee,

        discount: roundMoney(actualDiscount),

        final_amount: finalAmount,

        due_amount: roundMoney(dueAmount),
      };
    });

    setFeeItems(updatedFees);
  };
  const handleDiscountTypeChange = (type) => {
    setDiscountData((prev) => ({
      ...prev,
      type,
    }));

    calculateDiscount(type, discountData.value);
  };

  const handleDiscountValueChange = (value) => {
    const numValue = value === "" ? "" : Math.max(0, Number(value));

    setDiscountData((prev) => ({
      ...prev,
      value: numValue,
    }));

    calculateDiscount(discountData.type, numValue);
  };

  const handleUpdateDiscount = async () => {
    if (!feeItems.length) {
      SweetAlertShow("info", "Warning", "No fee details found.");
      return;
    }

    setSaving(true);

    try {
      const formData = new FormData();
      formData.append("bus_student_id", entry.id);
      formData.append("discount_type", discountData.type);
      formData.append("discount_value", discountData.value || 0);
      formData.append("discount_remark", discountRemark);

      feeItems.forEach((fee, index) => {
        formData.append(`fee_id[${index}]`, fee.id);
        formData.append(`total[${index}]`, fee.actual_amount);
        formData.append(`discount[${index}]`, fee.discount);
        formData.append(`final[${index}]`, fee.final_amount);
        formData.append(`paid[${index}]`, fee.paid_amount);
        formData.append(`due[${index}]`, fee.due_amount);
      });

      
      const response = await studentTransportNFeeUpdateDiscount(formData);

      if (response?.data?.status) {
        SweetAlertShow(
          "success",
          "Successfully",
          response?.data?.message || "Transport discount updated successfully.",
        );
        setMode("view");
        onUpdated?.(response?.data);
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to update discount.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message ??
          "Something went wrong!",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal d-block"
      style={{
        background: "rgba(0,0,0,0.5)",
      }}
      tabIndex={-1}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        onClick={(e) => e?.stopPropagation()}
      >
        <div className="modal-content rounded-4 border-0 overflow-hidden">
          <div className="modal-header border-0 pb-0">
            <div>
              <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                <i className="ri-wallet-3-line text-primary"></i>
                Transport Fee Details
              </h5>
              <div className="d-flex flex-wrap gap-2 mt-2">
                <Pill
                  icon="ri-bus-2-line"
                  color="primary"
                  label="Bus"
                  value={entry?.bus?.bus_number || "-"}
                />
                <Pill
                  icon="ri-map-pin-line"
                  color="success"
                  label="Stop"
                  value={entry?.stop?.stop_name || "-"}
                />
                <span
                  className={`badge rounded-pill bg-${statusMeta.color} d-inline-flex align-items-center gap-1 px-3 py-2`}
                >
                  <i className={statusMeta.icon}></i>
                  {statusMeta.label}
                </span>
              </div>
            </div>

            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={saving}
            ></button>
          </div>
          <div className="modal-body">
            {mode === "view" && (
              <>
                <div className="row g-3 mb-3">
                  <div className="col-6 col-md-3">
                    <div
                      className="p-2 rounded-3 text-center"
                      style={{
                        background: "#f8f9fb",
                      }}
                    >
                      <small className="text-secondary d-block">
                        Total Fee
                      </small>

                      <span className="fw-semibold">
                        {fmtMoney(entry?.total_fee)}
                      </span>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="p-2 rounded-3 text-center bg-warning bg-opacity-10">
                      <small className="text-warning d-block">Discount</small>

                      <span className="fw-semibold text-warning">
                        {fmtMoney(entry?.total_discount)}
                      </span>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div
                      className="p-2 rounded-3 text-center"
                      style={{
                        background: "#f8f9fb",
                      }}
                    >
                      <small className="text-secondary d-block">
                        Final Fee
                      </small>

                      <span className="fw-semibold">
                        {fmtMoney(entry?.total_final_fee)}
                      </span>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="p-2 rounded-3 text-center bg-success bg-opacity-10">
                      <small className="text-success d-block">Paid</small>
                      <span className="fw-semibold text-success">
                        {fmtMoney(entry?.total_paid)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-6 col-md-4">
                    <div className="p-2 rounded-3 text-center bg-danger bg-opacity-10">
                      <small className="text-danger d-block">Total Due</small>

                      <span className="fw-semibold text-danger">
                        {fmtMoney(entry?.total_due)}
                      </span>
                    </div>
                  </div>

                  <div className="col-6 col-md-4">
                    <div className="p-2 rounded-3 text-center bg-label-primary bg-opacity-10">
                      <small className="text-primary d-block">
                        Payment Type
                      </small>

                      <span className="fw-semibold text-primary">
                        {entry?.payment_type || "-"}
                      </span>
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <div className="p-2 rounded-3 text-center bg-info bg-opacity-10">
                      <small className="text-info d-block">Stop Fee</small>

                      <span className="fw-semibold text-info">
                        {fmtMoney(entry?.bus_stop_fee)}
                      </span>
                    </div>
                  </div>
                </div>

                {entry?.discount_remark && (
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <i className="ri-message-2-line text-secondary"></i>
                    <small className="text-secondary">
                      Discount Remark:
                      <span className="text-dark ms-1">
                        {entry.discount_remark}
                      </span>
                    </small>
                  </div>
                )}

                {feeDetails.length > 0 && (
                  <>
                    <small className="text-secondary fw-semibold d-flex align-items-center gap-1 mb-2">
                      <i className="ri-file-list-3-line"></i>
                      FEE BREAKDOWN
                    </small>
                    <div className="table-responsive mb-3">
                      <table className="table table-sm table-hover align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Period</th>
                            <th>Actual</th>
                            <th>Discount</th>
                            <th>Final</th>
                            <th>Paid</th>
                            <th>Due</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {feeDetails?.map((fee) => {
                            const meta = transportFeeStatusMeta(
                              fee.paid_amount,
                              fee.final_amount,
                            );

                            return (
                              <tr key={fee.id}>
                                <td>
                                  <span className="d-flex align-items-center gap-1">
                                    <i className="ri-calendar-2-line text-secondary"></i>
                                    {monthName(fee.month)} {fee.year}
                                    <span className="text-muted ms-1">
                                      ({fee.type})
                                    </span>
                                  </span>
                                </td>

                                <td>{fmtMoney(fee.actual_amount)}</td>

                                <td className="text-warning">
                                  {fmtMoney(fee.discount)}
                                </td>

                                <td className="fw-semibold">
                                  {fmtMoney(fee.final_amount)}
                                </td>

                                <td className="text-success">
                                  {fmtMoney(fee.paid_amount)}
                                </td>

                                <td className="text-danger">
                                  {fmtMoney(fee.due_amount)}
                                </td>

                                <td>
                                  <span
                                    className={`badge bg-${meta.color} bg-opacity-10 text-${meta.color} d-inline-flex align-items-center gap-1`}
                                  >
                                    <i className={meta.icon}></i>

                                    {meta.label}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>

                        <tfoot>
                          <tr className="fw-semibold">
                            <td>Total</td>

                            <td>
                              {fmtMoney(
                                feeDetails.reduce(
                                  (sum, fee) => sum + num(fee.actual_amount),
                                  0,
                                ),
                              )}
                            </td>

                            <td className="text-warning">
                              {fmtMoney(
                                feeDetails.reduce(
                                  (sum, fee) => sum + num(fee.discount),
                                  0,
                                ),
                              )}
                            </td>

                            <td>
                              {fmtMoney(
                                feeDetails.reduce(
                                  (sum, fee) => sum + num(fee.final_amount),
                                  0,
                                ),
                              )}
                            </td>

                            <td className="text-success">
                              {fmtMoney(
                                feeDetails.reduce(
                                  (sum, fee) => sum + num(fee.paid_amount),
                                  0,
                                ),
                              )}
                            </td>

                            <td className="text-danger">
                              {fmtMoney(
                                feeDetails.reduce(
                                  (sum, fee) => sum + num(fee.due_amount),
                                  0,
                                ),
                              )}
                            </td>

                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </>
                )}

                {/* OTHER INFO */}

                <div
                  className="d-flex flex-wrap gap-3 pt-2"
                  style={{
                    borderTop: "1px solid #f0f0f0",
                  }}
                >
                  <small className="text-secondary d-flex align-items-center gap-1">
                    <i className="ri-user-star-line text-primary"></i>
                    Driver:
                    <span className="fw-medium text-dark">
                      {entry?.bus?.driver_name || "-"}
                    </span>
                  </small>

                  <small className="text-secondary d-flex align-items-center gap-1">
                    <i className="ri-calendar-event-line text-primary"></i>
                    Joined:
                    <span className="fw-medium text-dark">
                      {formatToDate(entry?.joining_date)}
                    </span>
                  </small>

                  {entry?.end_date && (
                    <small className="text-secondary d-flex align-items-center gap-1">
                      <i className="ri-calendar-close-line text-danger"></i>
                      End:
                      <span className="fw-medium text-dark">
                        {formatToDate(entry?.end_date)}
                      </span>
                    </small>
                  )}
                </div>
              </>
            )}

            {/* =================================================
                DISCOUNT MODE
            ================================================== */}

            {mode === "discount" && (
              <div>
                {/* DISCOUNT INPUT */}

                <div className="row g-3 mb-3">
                  {/* TYPE */}

                  <div className="col-md-6">
                    <label className="form-label fw-semibold d-flex align-items-center gap-1">
                      <i className="ri-price-tag-3-line text-warning"></i>
                      Discount Type
                    </label>

                    <select
                      className="form-select"
                      value={discountData.type}
                      disabled={saving}
                      onChange={(e) => handleDiscountTypeChange(e.target.value)}
                    >
                      <option value="fixed">Fixed ₹</option>

                      <option value="percentage">Percentage %</option>
                    </select>
                  </div>

                  {/* VALUE */}

                  <div className="col-md-6">
                    <label className="form-label fw-semibold d-flex align-items-center gap-1">
                      <i className="ri-coupon-line text-warning"></i>

                      {discountData.type === "fixed"
                        ? "Discount ₹"
                        : "Discount %"}
                    </label>

                    <input
                      type="number"
                      className="form-control"
                      value={discountData.value}
                      onChange={(e) =>
                        handleDiscountValueChange(e.target.value)
                      }
                      placeholder="Enter discount value"
                      min="0"
                      max={discountData.type === "percentage" ? 100 : undefined}
                      disabled={saving}
                    />

                    {/* HELP TEXT */}

                    <small className="text-muted">
                      {discountData.type === "fixed"
                        ? "This amount will be applied separately to every month."
                        : "This percentage will be applied separately to every month."}
                    </small>
                  </div>
                </div>

                {/* EXAMPLE */}

                {discountData.value !== "" && num(discountData.value) > 0 && (
                  <div className="alert alert-warning py-2 px-3 mb-3">
                    <div className="d-flex align-items-start gap-2">
                      <i className="ri-information-line mt-1"></i>

                      <div>
                        <strong>Discount Preview</strong>

                        <div className="small mt-1">
                          {discountData.type === "fixed"
                            ? `₹${num(discountData.value).toLocaleString(
                                "en-IN",
                              )} discount will be applied to each month.`
                            : `${num(
                                discountData.value,
                              )}% discount will be applied to each month.`}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* FEE TABLE */}

                {feeItems.length > 0 && (
                  <div className="table-responsive mb-3">
                    <table className="table table-sm table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Period</th>

                          <th>Actual</th>

                          <th>Discount</th>

                          <th>Final</th>

                          <th>Paid</th>

                          <th>Due</th>
                        </tr>
                      </thead>

                      <tbody>
                        {feeItems.map((fee) => {
                          const rowDiscount = num(fee.discount);

                          return (
                            <tr key={fee.id}>
                              <td>
                                <span className="d-flex align-items-center gap-1">
                                  <i className="ri-calendar-2-line text-secondary"></i>

                                  {fee.label}
                                </span>
                              </td>

                              <td>{fmtMoney(fee.actual_amount)}</td>

                              <td className="text-warning fw-semibold">
                                {fmtMoney(rowDiscount)}
                              </td>

                              <td className="fw-bold">
                                {fmtMoney(fee.final_amount)}
                              </td>

                              <td className="text-success">
                                {fmtMoney(fee.paid_amount)}
                              </td>

                              <td className="text-danger">
                                {fmtMoney(fee.due_amount)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>

                      {/* TOTAL */}

                      <tfoot>
                        <tr className="fw-semibold">
                          <td>Total</td>

                          <td>{fmtMoney(totalActual)}</td>

                          <td className="text-warning">
                            {fmtMoney(totalDiscount)}
                          </td>

                          <td>{fmtMoney(totalFinal)}</td>

                          <td className="text-success">
                            {fmtMoney(totalPaid)}
                          </td>

                          <td className="text-danger">{fmtMoney(totalDue)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                {/* DISCOUNT REMARK */}

                <label className="form-label fw-semibold d-flex align-items-center gap-1">
                  <i className="ri-message-2-line text-secondary"></i>
                  Discount Remark
                </label>

                <textarea
                  className="form-control mb-3"
                  rows={2}
                  value={discountRemark}
                  onChange={(e) => setDiscountRemark(e.target.value)}
                  placeholder="Reason for discount"
                  disabled={saving}
                ></textarea>

                {/* ACTIONS */}

                <div className="d-flex gap-2 justify-content-end">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setMode("view")}
                    disabled={saving}
                  >
                    <i className="ri-close-line me-1"></i>
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={handleUpdateDiscount}
                    disabled={saving || !feeItems.length}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="ri-save-line me-1"></i>
                        Save Discount
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* =================================================
              FOOTER
          ================================================== */}

          {mode === "view" && (
            <div className="modal-footer border-0 pt-0">
              <button
                type="button"
                className="btn btn-outline-warning"
                onClick={() => setMode("discount")}
              >
                <i className="ri-coupon-line me-1"></i>
                Update Discount
              </button>

              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                <i className="ri-close-line me-1"></i>
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
